import { Button, TextInput } from "@twioku/common_components";
import { Answerer } from "../../../api/generated";
import { AnswererProfileImage } from "../../../components/AnswererProfileImage";
import { Controller } from "react-hook-form";
import { useUserQuestionForm } from "../hooks/useUserQuestionForm";
import { AddQuestionInput } from "../zod/AddQuestion";
import { Link } from "react-router-dom";

type UpdateProps = {
    mode: "update";
    handleClickDeleteQuestion: () => void;
    isDeleteLoading: boolean;
};
type CreateProps = {
    mode: "create";
};
type Props = {
    answerer: Answerer;
    handleClickSaveQuestion: (input: AddQuestionInput) => void;
    saveQuestionButtonName: string;
    isSaveLoading: boolean;
    defaultValue?: Omit<AddQuestionInput, "answererId">;
} & (UpdateProps | CreateProps);

export const QuestionForm = (props: Props) => {
    const {
        answerer,
        handleClickSaveQuestion,
        saveQuestionButtonName,
        isSaveLoading,
        defaultValue,
        mode,
    } = props;

    const { control, handleSubmit, isValid, isDirty } = useUserQuestionForm(
        answerer.answererId,
        defaultValue?.title
    );

    const action = mode === "update" ? "更新" : "追加";

    return (
        <div className="px-4 pb-8 pt-2">
            <div className="mb-4 flex items-center gap-2">
                <AnswererProfileImage
                    answererId={answerer.answererId}
                    src={answerer.profileImagePath}
                    imageSize="medium"
                />
                <p className="text-sm">
                    {answerer.name}さん 向けの質問{action}
                </p>
            </div>

            <div className="ml-1 mt-5">
                <p className="mb-2 text-sm text-gray-600">
                    ツイオクから届く定番の質問に加えて、{answerer.name}
                    さんに向けたオリジナル質問を追加できます。
                    <br />
                    ツイオクを利用していて聞いてみたいことが思いついたら、こちらから質問を追加してみましょう！
                </p>
                <span className="text-lg">{action}する質問</span>
                <div className="mt-2">
                    <Controller
                        name="title"
                        control={control}
                        render={({ field, fieldState }) => (
                            <>
                                <TextInput
                                    type="text"
                                    name={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    className="mx-4 h-full w-full rounded-lg border pl-6 pr-4"
                                    maxRows={3}
                                    rows={3}
                                    multiline
                                    placeholder={`例)\n最近寝付きが悪いと言っていたけど、その後体調は大丈夫ですか？`}
                                />
                                <span className="block text-base text-red-500">
                                    {fieldState.error?.message}
                                </span>
                            </>
                        )}
                    />
                </div>
                <span className="text-xs text-gray-600">
                    {action}した質問は次回以降の送信タイミングで送信されます。
                    <br />
                    オリジナル質問は通常質問より優先されます。
                </span>
            </div>

            {mode === "update" ? (
                <div className="fixed bottom-0 left-1/2 w-full -translate-x-1/2 bg-white text-center">
                    <div>
                        <Button
                            onClick={handleSubmit(handleClickSaveQuestion)}
                            className="mb-10 mt-2 h-12 w-11/12"
                            disabled={
                                !isDirty || !isValid || props.isDeleteLoading
                            }
                            loading={isSaveLoading}
                        >
                            {saveQuestionButtonName}
                        </Button>
                    </div>
                    <div className="mb-6">
                        <Link
                            to="#"
                            className="mr-4 text-sm text-red-500 underline"
                            onClick={props.handleClickDeleteQuestion}
                        >
                            削除する
                        </Link>
                    </div>
                </div>
            ) : (
                <div className="fixed bottom-0 left-1/2 w-full -translate-x-1/2 bg-white text-center">
                    <Button
                        onClick={handleSubmit(handleClickSaveQuestion)}
                        className="mb-8 mt-2 h-12 w-11/12"
                        disabled={!isDirty || !isValid}
                        loading={isSaveLoading}
                    >
                        {saveQuestionButtonName}
                    </Button>
                </div>
            )}
        </div>
    );
};
